import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Form,
  Image,
  Input,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Typography,
  Upload,
  message,
} from "antd";
import { PlusOutlined, UploadOutlined, DeleteOutlined, EditOutlined, CheckOutlined } from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useSelector } from "react-redux";
import {
  apiGetClubSite,
  apiSaveClubSite,
  apiGetClubHomepage,
  apiSaveClubHomepage,
  apiDeleteClubHomepage,
  apiGetSubdomainFiles,
} from "services/customWebpageService";

const { Title, Text } = Typography;
const { TextArea } = Input;

const CustomWebpagePage = () => {
  const [siteForm] = Form.useForm();
  const [sectionForm] = Form.useForm();
  const userDataStore = useSelector((x) => x.userDataStore);
  const appDataStore = useSelector((x) => x.appDataStore);
  const clubId = appDataStore?.club?.id;

  const [siteLoading, setSiteLoading] = useState(false);
  const [siteInfoPage, setSiteInfoPage] = useState(false);
  const [siteInfoPageFile, setSiteInfoPageFile] = useState("");
  const [siteAvailableFiles, setSiteAvailableFiles] = useState([]);
  const [sectionLoading, setSectionLoading] = useState(false);
  const [sections, setSections] = useState([]);
  const [logoLeftFile, setLogoLeftFile] = useState(null);
  const [logoRightFile, setLogoRightFile] = useState(null);
  const [logoLeftPreview, setLogoLeftPreview] = useState("");
  const [logoRightPreview, setLogoRightPreview] = useState("");
  const [sectionModalVisible, setSectionModalVisible] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [sectionImageFile, setSectionImageFile] = useState(null);
  const [infoPageEnabled, setInfoPageEnabled] = useState(false);
  const [infoPageContent, setInfoPageContent] = useState("");
  const [infoPageType, setInfoPageType] = useState("custom");
  const [infoPageFile, setInfoPageFile] = useState("");
  const [availableFiles, setAvailableFiles] = useState([]);

  const toAbsoluteUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    const base = process.env.REACT_APP_API?.replace("/api/", "") || "";
    return `${base}/${url}`;
  };

  const disabledSiteActions = useMemo(() => !userDataStore?.token || !clubId, [userDataStore?.token, clubId]);

  useEffect(() => {
    if (!disabledSiteActions) {
      loadSite();
      loadSections();
    }
  }, [disabledSiteActions]);

  const loadSite = async () => {
    setSiteLoading(true);
    try {
      const res = await apiGetClubSite({ token: userDataStore.token, club_id: clubId });
      if (res) {
        siteForm.setFieldsValue({
          domain: res.domain || "",
          site_title: res.site_title || "",
          site_subtitle: res.site_subtitle || "",
        });
        const infoPageOn = res.info_page === "1" || res.info_page === 1;
        setSiteInfoPage(infoPageOn);
        setSiteInfoPageFile(res.info_page_file || "");
        if (infoPageOn) {
          // pre-load available files so the dropdown is populated
          setTimeout(() => loadSiteAvailableFiles(), 0);
        }
        setLogoLeftPreview(toAbsoluteUrl(res.logo_left) || "");
        setLogoRightPreview(toAbsoluteUrl(res.logo_right) || "");
      }
    } catch (err) {
      message.error(err || "Unable to load site settings");
    } finally {
      setSiteLoading(false);
    }
  };

  const loadSections = async () => {
    setSectionLoading(true);
    try {
      const res = await apiGetClubHomepage({ token: userDataStore.token, club_id: clubId });
      console.log('Homepage sections loaded:', res);
      setSections(Array.isArray(res) ? res : []);
    } catch (err) {
      message.error(err || "Unable to load homepage sections");
    } finally {
      setSectionLoading(false);
    }
  };

  const handleSaveSite = async () => {
    try {
      const values = await siteForm.validateFields();
      setSiteLoading(true);
      await apiSaveClubSite({
        token: userDataStore.token,
        club_id: clubId,
        domain: values.domain,
        site_title: values.site_title,
        site_subtitle: values.site_subtitle,
        info_page: siteInfoPage ? 1 : 0,
        info_page_file: siteInfoPageFile || "",
        logo_left: logoLeftFile ?? logoLeftPreview,
        logo_right: logoRightFile ?? logoRightPreview,
      });
      message.success("Website settings saved");
      setLogoLeftFile(null);
      setLogoRightFile(null);
      loadSite();
    } catch (err) {
      if (err?.errorFields) return; // validation errors already shown
      message.error(err || "Unable to save website settings");
    } finally {
      setSiteLoading(false);
    }
  };

  const resetSectionModal = () => {
    sectionForm.resetFields();
    setSectionImageFile(null);
    setEditingSection(null);
    setInfoPageEnabled(false);
    setInfoPageContent("");
    setInfoPageType("custom");
    setInfoPageFile("");
  };

  const loadAvailableFiles = async () => {
    try {
      const domain = siteForm.getFieldValue("domain");
      if (!domain) return;
      const res = await apiGetSubdomainFiles({ token: userDataStore.token, domain });
      setAvailableFiles(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error("Failed to load subdomain files:", err);
      setAvailableFiles([]);
    }
  };

  const loadSiteAvailableFiles = async () => {
    try {
      const domain = siteForm.getFieldValue("domain");
      if (!domain) return;
      const res = await apiGetSubdomainFiles({ token: userDataStore.token, domain, subfolder: "page" });
      setSiteAvailableFiles(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error("Failed to load site page files:", err);
      setSiteAvailableFiles([]);
    }
  };

  const openNewSection = () => {
    resetSectionModal();
    loadAvailableFiles();
    setSectionModalVisible(true);
  };

  const openEditSection = (record) => {
    setEditingSection(record);
    sectionForm.setFieldsValue({
      title: record.title || "",
      description: record.description || "",
      font_color: record.font_color || "",
    });
    setSectionImageFile(null);
    setInfoPageEnabled(record.info_page_enabled === "1" || record.info_page_enabled === 1);
    setInfoPageContent(record.info_page_content || "");
    setInfoPageType(record.info_page_type || "custom");
    setInfoPageFile(record.info_page_file || "");
    loadAvailableFiles();
    setSectionModalVisible(true);
  };

  const handleSaveSection = async () => {
    try {
      const values = await sectionForm.validateFields();
      setSectionLoading(true);
      await apiSaveClubHomepage({
        token: userDataStore.token,
        club_id: clubId,
        id: editingSection?.id,
        title: values.title,
        description: values.description,
        font_color: values.font_color,
        image: sectionImageFile,
        info_page_enabled: infoPageEnabled ? 1 : 0,
        info_page_content: infoPageContent,
        info_page_type: infoPageType,
        info_page_file: infoPageFile,
      });
      message.success(editingSection ? "Section updated" : "Section added");
      setSectionModalVisible(false);
      resetSectionModal();
      loadSections();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err || "Unable to save section");
    } finally {
      setSectionLoading(false);
    }
  };

  const handleDeleteSection = (record) => {
    Modal.confirm({
      title: "Delete this section?",
      onOk: async () => {
        try {
          setSectionLoading(true);
          await apiDeleteClubHomepage({ token: userDataStore.token, club_id: clubId, id: record.id });
          message.success("Section deleted");
          loadSections();
        } catch (err) {
          message.error(err || "Unable to delete section");
        } finally {
          setSectionLoading(false);
        }
      },
    });
  };

  const leftFileList = useMemo(() => {
    if (logoLeftFile) {
      return [{ uid: "left-new", name: logoLeftFile.name, status: "done", url: logoLeftPreview || URL.createObjectURL(logoLeftFile) }];
    }
    if (logoLeftPreview) {
      return [{ uid: "left", name: logoLeftPreview.split("/").pop(), status: "done", url: logoLeftPreview }];
    }
    return [];
  }, [logoLeftFile, logoLeftPreview]);

  const rightFileList = useMemo(() => {
    if (logoRightFile) {
      return [{ uid: "right-new", name: logoRightFile.name, status: "done", url: logoRightPreview || URL.createObjectURL(logoRightFile) }];
    }
    if (logoRightPreview) {
      return [{ uid: "right", name: logoRightPreview.split("/").pop(), status: "done", url: logoRightPreview }];
    }
    return [];
  }, [logoRightFile, logoRightPreview]);

  const siteUploadProps = (setter, previewSetter, fileList) => ({
    beforeUpload: (file) => {
      setter(file);
      previewSetter(URL.createObjectURL(file));
      return false; // prevent auto upload
    },
    onRemove: () => {
      setter(null);
      previewSetter("");
    },
    maxCount: 1,
    accept: "image/*",
    fileList,
    listType: "picture",
    showUploadList: false,
  });

  const sectionColumns = [
    { title: "Title", dataIndex: "title", key: "title" },
    { title: "Description", dataIndex: "description", key: "description", ellipsis: true },
    { title: "Font Color", dataIndex: "font_color", key: "font_color" },
    {
      title: "Info Page",
      dataIndex: "info_page_enabled",
      key: "info_page_enabled",
      width: 100,
      align: "center",
      render: (enabled, record) => {
        if (enabled === "1" || enabled === 1) {
          const type = record.info_page_type === "fixed" ? "Fixed" : "Custom";
          return <span style={{ color: "#52c41a" }}>{type}</span>;
        }
        return "-";
      },
    },
    {
      title: "Image",
      dataIndex: "image_url",
      key: "image_url",
      width: 120,
      render: (url, record) => {
        console.log('Image render - url:', url, 'record:', record);
        if (!url) return "-";
        
        // If URL doesn't start with http, prepend the API base URL
        const imageUrl = url.startsWith('http') ? url : `${process.env.REACT_APP_API?.replace('/api/', '')}/${url}`;
        
        return (
          <Image 
            src={imageUrl} 
            alt="section thumbnail"
            preview={{
              mask: "Preview",
            }}
            style={{ 
              width: 80, 
              height: 60, 
              objectFit: "cover",
              borderRadius: 4,
              cursor: "pointer"
            }} 
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg=="
          />
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditSection(record)} />
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteSection(record)} />
        </Space>
      ),
    },
  ];

  if (!clubId) {
    return (
      <Card>
        <Title level={4}>Custom Webpage</Title>
        <Text>Please select a club first.</Text>
      </Card>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Custom Webpage</Title>
      <Text type="secondary">Manage your public Website and Homepage blocks.</Text>
      <Divider />

      <Card title="Website Configuration" loading={siteLoading} style={{ marginBottom: 24 }}>
        <Form layout="vertical" form={siteForm}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Domain"
                name="domain"
                rules={[{ required: true, message: "Domain is required" }]}
              >
                <Input placeholder="e.g. myclub.beclutch.club" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Website Title"
                name="site_title"
                rules={[{ required: true, message: "Website title is required" }]}
              >
                <Input placeholder="Website title" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Website Subtitle" name="site_subtitle">
                <Input placeholder="Optional subtitle" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} style={{ display: "flex", alignItems: "center", paddingTop: 30 }}>
              <Checkbox
                checked={siteInfoPage}
                onChange={(e) => {
                  setSiteInfoPage(e.target.checked);
                  if (e.target.checked) loadSiteAvailableFiles();
                  else setSiteInfoPageFile("");
                }}
              >
                Info Page
              </Checkbox>
              {siteInfoPage && (
                <Select
                  value={siteInfoPageFile || undefined}
                  onChange={(value) => setSiteInfoPageFile(value || "")}
                  placeholder="Select HTML page"
                  style={{ marginLeft: 12, flex: 1 }}
                  allowClear
                  onClear={() => setSiteInfoPageFile("")}
                >
                  {siteAvailableFiles.map((file) => (
                    <Select.Option key={file} value={file}>
                      {file}
                    </Select.Option>
                  ))}
                </Select>
              )}
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Left Logo">
                <Upload
                  {...siteUploadProps(setLogoLeftFile, setLogoLeftPreview, leftFileList)}
                >
                  <Button icon={<UploadOutlined />}>Select Left Logo</Button>
                </Upload>
                {logoLeftPreview && (
                  <div style={{ marginTop: 12 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Preview:</Text>
                    <div style={{ marginTop: 8 }}>
                      <Image 
                        src={logoLeftPreview} 
                        alt="left logo"
                        style={{ maxHeight: 80, maxWidth: 150, objectFit: "contain" }}
                      />
                    </div>
                  </div>
                )}
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Right Logo">
                <Upload
                  {...siteUploadProps(setLogoRightFile, setLogoRightPreview, rightFileList)}
                >
                  <Button icon={<UploadOutlined />}>Select Right Logo</Button>
                </Upload>
                {logoRightPreview && (
                  <div style={{ marginTop: 12 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Preview:</Text>
                    <div style={{ marginTop: 8 }}>
                      <Image 
                        src={logoRightPreview} 
                        alt="right logo"
                        style={{ maxHeight: 80, maxWidth: 150, objectFit: "contain" }}
                      />
                    </div>
                  </div>
                )}
              </Form.Item>
            </Col>
          </Row>
          <Button type="primary" onClick={handleSaveSite} loading={siteLoading} disabled={disabledSiteActions}>
            Save Website
          </Button>
        </Form>
      </Card>

      <Card
        title="Homepage Sections"
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={openNewSection} disabled={disabledSiteActions}>Add Section</Button>}
        loading={sectionLoading}
      >
        <Table
          dataSource={sections.map((item) => ({ ...item, key: item.id }))}
          columns={sectionColumns}
          pagination={false}
        />
      </Card>

      <Modal
        visible={sectionModalVisible}
        title={editingSection ? "Edit Section" : "Add Section"}
        onCancel={() => {
          setSectionModalVisible(false);
          resetSectionModal();
        }}
        onOk={handleSaveSection}
        confirmLoading={sectionLoading}
        destroyOnClose
        width={infoPageEnabled ? 800 : 520}
      >
        <Form layout="vertical" form={sectionForm}>
          <Form.Item name="title" label="Title" rules={[{ required: true, message: "Title is required" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Button Text">
            <TextArea rows={3} placeholder="ie REGISTER or APPLY" />
          </Form.Item>
          <Form.Item label="Font Color" style={{ marginBottom: 0 }}>
            <Space>
              <Form.Item name="font_color" noStyle>
                <Input placeholder="#000000" style={{ width: 120 }} />
              </Form.Item>
              <input
                type="color"
                value={sectionForm.getFieldValue('font_color') || '#000000'}
                onChange={(e) => {
                  sectionForm.setFieldsValue({ font_color: e.target.value });
                }}
                style={{ width: 40, height: 32, padding: 0, border: '1px solid #d9d9d9', borderRadius: 4, cursor: 'pointer' }}
              />
            </Space>
          </Form.Item>
          <Form.Item label="Image">
            <Upload
              beforeUpload={(file) => {
                setSectionImageFile(file);
                return false;
              }}
              onRemove={() => setSectionImageFile(null)}
              maxCount={1}
              accept="image/*"
              listType="picture"
            >
              <Button icon={<UploadOutlined />}>Select Image</Button>
            </Upload>
            <Text type="secondary">Naming handled server-side as club_id + "db-item-" + id.</Text>
          </Form.Item>
          <Divider />
          <Form.Item label="Enable Info Page">
            <Space>
              <Switch
                checked={infoPageEnabled}
                onChange={(checked) => setInfoPageEnabled(checked)}
              />
              {infoPageEnabled && (
                <Radio.Group
                  value={infoPageType}
                  onChange={(e) => setInfoPageType(e.target.value)}
                  style={{ marginLeft: 16 }}
                >
                  <Radio value="custom">Custom</Radio>
                  <Radio value="fixed">Fixed</Radio>
                </Radio.Group>
              )}
            </Space>
            <div>
              <Text type="secondary">
                Show a custom info page before the registration list
              </Text>
            </div>
          </Form.Item>
          {infoPageEnabled && infoPageType === "fixed" && (
            <Form.Item label="Select Info Page File">
              <Select
                value={infoPageFile}
                onChange={(value) => setInfoPageFile(value)}
                placeholder="Select an HTML or PHP file"
                style={{ width: "100%" }}
                allowClear
              >
                {availableFiles.map((file) => (
                  <Select.Option key={file} value={file}>
                    {file}
                  </Select.Option>
                ))}
              </Select>
              <Text type="secondary" style={{ marginTop: 8, display: "block" }}>
                Select an existing HTML or PHP file from your subdomain directory.
              </Text>
            </Form.Item>
          )}
          {infoPageEnabled && infoPageType === "custom" && (
            <Form.Item label="Info Page Content">
              <ReactQuill
                theme="snow"
                value={infoPageContent}
                onChange={setInfoPageContent}
                style={{ minHeight: 200 }}
                modules={{
                  toolbar: [
                    [{ header: [1, 2, 3, false] }],
                    ["bold", "italic", "underline", "strike"],
                    [{ color: [] }, { background: [] }],
                    [{ list: "ordered" }, { list: "bullet" }],
                    [{ align: [] }],
                    ["link", "image"],
                    ["clean"],
                  ],
                }}
              />
              <Text type="secondary" style={{ marginTop: 8, display: "block" }}>
                Create the content users will see before viewing the registration list. Include a call-to-action.
              </Text>
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default CustomWebpagePage;
